// The app's single reaction to a lapsed `loom_session` cookie, registered once
// in `main.tsx` (`setUnauthorizedHandler`). `beforeLoad` only runs on a route
// MATCH, so a click inside an already-mounted surface (the history rail's pick,
// the dashboard's fetches) never reaches it — the 401 was swallowed by whatever
// the calling hook rendered, which for the rail was nothing.
//
// A SOFT router navigation, not `window.location.assign`: a hard document load
// would unload the page out from under the surface that just failed, and
// importing the router into `lib/http.ts` would be a module cycle. The
// `signalled` latch in `lib/http` bounds this to once per lapse; `navigating`
// bounds it again across the navigation's own await.
//
// The shared `/auth/me` answer is dropped FIRST, on every call: the sign-in it
// holds just ended, and a guard reading it would let the user back in for up to
// `ME_TTL_MS`. Only a `no_session` 401 gets here — a `reauth_required` one (a
// dead GitLab token under a good Loom sign-in) is filtered out in `lib/http`.

import { invalidateMe } from "./useAuth";

/** The slice of the router this handler uses, so a test can pass a double. */
export interface UnauthorizedRouter {
  readonly state: { readonly location: { readonly pathname: string } };
  navigate(opts: { readonly to: "/sign-in" }): Promise<unknown>;
}

export interface UnauthorizedOptions {
  /**
   * The Focus panel (`src/panel/`): it has no sign-in page to go to (the user
   * signs in again in the assistant), so it shows its own notice instead and
   * the router never moves. The web passes nothing.
   */
  readonly onSignedOut?: () => void;
}

export const makeUnauthorizedHandler = (
  router: UnauthorizedRouter,
  opts: UnauthorizedOptions = {},
): (() => void) => {
  let navigating = false;
  return () => {
    invalidateMe();
    if (opts.onSignedOut !== undefined) {
      opts.onSignedOut();
      return;
    }
    if (navigating) return;
    if (router.state.location.pathname === "/sign-in") return;
    navigating = true;
    void router.navigate({ to: "/sign-in" }).finally(() => {
      navigating = false;
    });
  };
};
